import { useState, useEffect } from "react";
import {
  fetchProposedDecisions,
  ProposedDecision,
} from "../../data/api/proposedDecisionsApi";

export interface UseProposedDecisionsReturn {
  proposedDecisions: ProposedDecision[];
  isLoadingProposedDecisions: boolean;
}

/**
 * Hook to fetch and manage proposed decisions for a given job ID
 * Automatically fetches when jobId changes
 *
 * @param jobId - The job ID (invoice_id) to fetch proposed decisions for
 * @returns Object containing proposedDecisions array and loading state
 */
export const useProposedDecisions = (
  jobId: string | null
): UseProposedDecisionsReturn => {
  const [proposedDecisions, setProposedDecisions] = useState<
    ProposedDecision[]
  >([]);
  const [isLoadingProposedDecisions, setIsLoadingProposedDecisions] =
    useState(false);

  useEffect(() => {
    const loadProposedDecisions = async () => {
      if (!jobId) {
        setProposedDecisions([]);
        return;
      }

      setIsLoadingProposedDecisions(true);
      try {
        const decisions = await fetchProposedDecisions(jobId);
        setProposedDecisions(decisions);
      } catch (error) {
        console.error("Failed to fetch proposed decisions:", error);
        // Silently fail - don't show error to user as this is optional data
        setProposedDecisions([]);
      } finally {
        setIsLoadingProposedDecisions(false);
      }
    };

    loadProposedDecisions();
  }, [jobId]);

  return {
    proposedDecisions,
    isLoadingProposedDecisions,
  };
};
